import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AccountCircle,
  ExitToApp,
  AdminPanelSettings,
  Business,
  Engineering,
  ShoppingCart,
  NotificationsActive,
  Security,
  Help,
  Language,
  DarkMode,
  LightMode,
  Dashboard,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRbac } from '../../context/RbacContext';

interface TopbarProps {
  onDrawerToggle: () => void;
  notificationsCount?: number;
}

export const Topbar: React.FC<TopbarProps> = ({ onDrawerToggle, notificationsCount = 0 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasPermission, hasRole } = useRbac();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleSettings = () => {
    if (hasPermission('admin:settings')) {
      navigate('/admin/settings');
    } else {
      navigate('/settings');
    }
    handleMenuClose();
  };

  const handleNotifications = () => {
    navigate('/notifications');
    handleNotificationClose();
  };

  const getRoleIcon = () => {
    if (hasRole('admin')) return <AdminPanelSettings color="error" />;
    if (hasRole('manager')) return <Business color="primary" />;
    if (hasRole('worker')) return <Engineering color="secondary" />;
    if (hasRole('customer')) return <ShoppingCart color="success" />;
    return <PersonIcon />;
  };

  const getRoleColor = () => {
    if (hasRole('admin')) return 'error';
    if (hasRole('manager')) return 'primary';
    if (hasRole('worker')) return 'secondary';
    if (hasRole('customer')) return 'success';
    return 'default';
  };

  const getRoleLabel = () => {
    if (hasRole('admin')) return 'Admin';
    if (hasRole('manager')) return 'Manager';
    if (hasRole('worker')) return 'Worker';
    if (hasRole('customer')) return 'Customer';
    return 'User';
  };

  const getCurrentPageTitle = () => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) return 'Admin Panel';
    if (path.startsWith('/worker/dashboard')) return 'Worker Dashboard';
    if (path.startsWith('/manager/dashboard')) return 'Manager Dashboard';
    if (path.startsWith('/dashboard')) return 'Dashboard';
    if (path.startsWith('/orders')) return 'Orders';
    if (path.startsWith('/inventory')) return 'Inventory';
    if (path.startsWith('/production')) return 'Production';
    if (path.startsWith('/maintenance')) return 'Maintenance';
    if (path.startsWith('/quality')) return 'Quality Control';
    if (path.startsWith('/customers')) return 'Customers';
    if (path.startsWith('/reports')) return 'Reports';
    if (path.startsWith('/customer-portal')) return 'Customer Portal';
    return 'Dashboard';
  };

  const handleDashboardNavigation = () => {
    if (hasRole('admin')) {
      navigate('/admin/dashboard');
    } else if (hasRole('manager')) {
      navigate('/manager/dashboard');
    } else if (hasRole('worker')) {
      navigate('/worker/dashboard');
    } else {
      navigate('/dashboard');
    }
    handleMenuClose();
  };

  return (
    <AppBar 
      position="static" 
      sx={{
        width: '100%',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.drawer + 1, // Ensure topbar is above sidebar
      }}
    >
      <Toolbar sx={{ minHeight: '64px' }}>
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {getCurrentPageTitle()}
        </Typography>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton
            color="inherit"
            sx={{ mr: 2 }}
            onClick={handleNotificationOpen}
          >
            <Badge badgeContent={notificationsCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Profile */}
        <Tooltip title="User Menu">
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
              },
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {user?.name?.[0] || user?.email?.[0] || 'U'}
            </Avatar>
            {!isMobile && (
              <Box sx={{ textAlign: 'left', display: 'none', md: 'block' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {user?.name || 'User'}
                </Typography>
                <Chip
                  icon={getRoleIcon()}
                  label={getRoleLabel()}
                  size="small"
                  color={getRoleColor() as any}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            )}
          </IconButton>
        </Tooltip>

        {/* User Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
              minWidth: 250,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {/* User Info Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar 
                sx={{ 
                  width: 48, 
                  height: 48,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {user?.name?.[0] || user?.email?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
                <Chip
                  icon={getRoleIcon()}
                  label={getRoleLabel()}
                  size="small"
                  color={getRoleColor() as any}
                  sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>

          {/* Menu Items */}
          <MenuItem onClick={handleDashboardNavigation}>
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary={
              hasRole('admin') ? 'Admin Dashboard' :
              hasRole('manager') ? 'Manager Dashboard' :
              hasRole('worker') ? 'Worker Dashboard' :
              'Dashboard'
            } />
          </MenuItem>

          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>

          {hasRole('admin') && (
            <MenuItem onClick={() => { navigate('/admin'); handleMenuClose(); }}>
              <ListItemIcon>
                <AdminPanelSettings />
              </ListItemIcon>
              <ListItemText primary="Admin Panel" />
            </MenuItem>
          )}

          <Divider />

          <MenuItem>
            <ListItemIcon>
              <Language />
            </ListItemIcon>
            <ListItemText primary="Language" />
          </MenuItem>

          <MenuItem>
            <ListItemIcon>
              <DarkMode />
            </ListItemIcon>
            <ListItemText primary="Theme" />
          </MenuItem>

          <MenuItem>
            <ListItemIcon>
              <Help />
            </ListItemIcon>
            <ListItemText primary="Help & Support" />
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <ExitToApp sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              mt: 1,
              minWidth: 300,
              maxHeight: 400,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {notificationsCount} unread notifications
            </Typography>
          </Box>
          
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No new notifications
            </Typography>
          </Box>

          <Divider />

          <MenuItem onClick={handleNotifications} sx={{ justifyContent: 'center' }}>
            <ListItemIcon>
              <NotificationsActive />
            </ListItemIcon>
            <ListItemText primary="View All Notifications" />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
